import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, FileDown, Loader2, ShieldCheck, ArrowLeft, LogOut } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import Brand from "../components/Brand";
import { api } from "../lib/api";
import { EignungStep, useEignungQuestions, isEignungComplete } from "../components/EignungStep";

const TOKEN_KEY = "we_staff_token";
const USER_KEY = "we_staff_user";

// Jobcoaching + Gründercoaching = UE-editable
// Weiterbildung = read-only Modulauswahl (feste UE/Preise)
const STAFF_TRACKS = ["coaching", "gruendercoaching", "weiterbildung"];
const EDITABLE_TRACKS = ["coaching", "gruendercoaching"];

export default function StaffOffer() {
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  let user = null;
  try { user = JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch {}
  const [authChecked, setAuthChecked] = useState(false);
  const [authValid, setAuthValid] = useState(!!token);

  // verify token on mount
  useEffect(() => {
    if (!token) { setAuthChecked(true); return; }
    api.get("/auth/staff/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        localStorage.setItem(USER_KEY, JSON.stringify(r.data.user));
        setAuthValid(true);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setAuthValid(false);
      })
      .finally(() => setAuthChecked(true));
  }, [token]);

  if (!authChecked) return null;
  if (!token || !authValid) return <Navigate to="/staff/login" replace />;

  return <StaffOfferForm user={user} token={token} />;
}

function StaffOfferForm({ user, token }) {
  const [tracks, setTracks] = useState([]);
  const eignungQuestions = useEignungQuestions();
  const [form, setForm] = useState({
    track: "coaching",
    full_name: "",
    email: "",
    phone: "",
    city: "",
    customer_number: "",
    selected_courses: [],
    custom_ue: {},
    custom_names: {},
    custom_prices: {},
    start_date: "",
    start_weeks_from: "",
    start_weeks_to: "",
    duration_weeks_from: "",
    duration_weeks_to: "",
    has_voucher: false,
    voucher_number: "",
    message: "",
    eignung_answers: {},
    erstberatung: "",
    source: "staff-manual",
  });
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const blobRef = useRef("");

  useEffect(() => {
    api.get("/tracks")
      .then((r) => setTracks(r.data.filter((t) => STAFF_TRACKS.includes(t.key))))
      .catch(() => toast.error("Tracks konnten nicht geladen werden"));
    return () => { if (blobRef.current) URL.revokeObjectURL(blobRef.current); };
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    window.location.href = "/staff/login";
  };

  const activeTrack = tracks.find((t) => t.key === form.track);
  const pricePerUE = activeTrack?.price_per_ue || 0;
  // Compute effective UE + effective price for a course key
  const effUE = (k) => {
    const course = activeTrack?.courses.find((c) => c.key === k);
    const custom = form.custom_ue[k];
    return custom != null ? Number(custom) : (course?.ue || 0);
  };
  const effPrice = (k) => {
    // If user manually set a custom price → use that; else UE × rate
    const custom = form.custom_prices[k];
    if (custom != null && custom !== "") return Number(custom);
    return effUE(k) * pricePerUE;
  };
  const totalUE = (form.selected_courses || []).reduce((s, k) => s + effUE(k), 0);
  const totalPrice = (form.selected_courses || []).reduce((s, k) => s + effPrice(k), 0);
  const hasAnyPrice = pricePerUE > 0 && form.selected_courses.length > 0;

  const toggleCourse = (k) => {
    setForm((f) => {
      const has = f.selected_courses.includes(k);
      const next = has ? f.selected_courses.filter((x) => x !== k) : [...f.selected_courses, k];
      const stripKey = (obj) => { const c = { ...obj }; delete c[k]; return c; };
      return next.includes(k)
        ? { ...f, selected_courses: next }
        : { ...f, selected_courses: next,
            custom_ue: stripKey(f.custom_ue),
            custom_names: stripKey(f.custom_names),
            custom_prices: stripKey(f.custom_prices) };
    });
  };

  const setCustomUE = (k, val) => {
    setForm((f) => ({ ...f, custom_ue: { ...f.custom_ue, [k]: val === "" ? undefined : Number(val) } }));
  };
  const setCustomName = (k, val) => {
    setForm((f) => ({ ...f, custom_names: { ...f.custom_names, [k]: val } }));
  };
  const setCustomPrice = (k, val) => {
    setForm((f) => ({ ...f, custom_prices: { ...f.custom_prices, [k]: val === "" ? undefined : Number(val) } }));
  };

  const clearCustomUE = (k) => {
    setForm((f) => {
      const c = { ...f.custom_ue }; delete c[k]; return { ...f, custom_ue: c };
    });
  };
  const clearCustomName = (k) => {
    setForm((f) => {
      const c = { ...f.custom_names }; delete c[k]; return { ...f, custom_names: c };
    });
  };
  const clearCustomPrice = (k) => {
    setForm((f) => {
      const c = { ...f.custom_prices }; delete c[k]; return { ...f, custom_prices: c };
    });
  };

  const canSubmit = () =>
    form.track &&
    form.selected_courses.length > 0 &&
    form.full_name.trim().length > 2 &&
    /.+@.+\..+/.test(form.email) &&
    form.phone.trim().length >= 5 &&
    form.city.trim().length >= 2 &&
    (form.erstberatung === "nein" ||
      (form.erstberatung === "ja" && isEignungComplete(eignungQuestions, form.track, form.eignung_answers)));

  const buildPayload = () => {
    const clean = (obj, cast) => {
      const out = {};
      Object.entries(obj || {}).forEach(([k, v]) => {
        if (v == null || v === "") return;
        if (cast === Number) {
          const n = Number(v);
          if (!isNaN(n)) out[k] = n;
        } else out[k] = String(v).trim();
      });
      return out;
    };
    const cleanUE = clean(form.custom_ue, Number);
    const cleanNames = {};
    Object.entries(form.custom_names || {}).forEach(([k, v]) => {
      if (v && v.trim()) {
        const course = activeTrack?.courses.find((c) => c.key === k);
        if (course && v.trim() !== course.name) cleanNames[k] = v.trim();
      }
    });
    // Auto-calc prices from UE × rate for all selected courses, unless user overrode
    const finalPrices = {};
    if (pricePerUE > 0) {
      for (const k of form.selected_courses) {
        const manual = form.custom_prices[k];
        if (manual != null && manual !== "") {
          finalPrices[k] = Number(manual);
        } else {
          finalPrices[k] = Math.round(effUE(k) * pricePerUE * 100) / 100;
        }
      }
    } else {
      Object.assign(finalPrices, clean(form.custom_prices, Number));
    }
    return {
      ...form,
      start_weeks_from: form.start_weeks_from === "" ? undefined : Number(form.start_weeks_from),
      start_weeks_to:   form.start_weeks_to   === "" ? undefined : Number(form.start_weeks_to),
      duration_weeks_from: form.duration_weeks_from === "" ? undefined : Number(form.duration_weeks_from),
      duration_weeks_to:   form.duration_weeks_to   === "" ? undefined : Number(form.duration_weeks_to),
      created_by_name: user?.name || undefined,
      created_by_email: user?.email || undefined,
      custom_ue: Object.keys(cleanUE).length ? cleanUE : undefined,
      custom_names: Object.keys(cleanNames).length ? cleanNames : undefined,
      custom_prices: Object.keys(finalPrices).length ? finalPrices : undefined,
    };
  };

  const generatePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.post("/leads/preview", buildPayload(), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
      blobRef.current = url;
      setPreviewUrl(url);
      setShowPreview(true);
    } catch {
      toast.error("Vorschau fehlgeschlagen");
    } finally {
      setPreviewLoading(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", buildPayload(), { headers: { Authorization: `Bearer ${token}` } });
      setLead(data);
      toast.success("Angebot erstellt & versendet ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setLead(null);
    setPreviewUrl(""); setShowPreview(false);
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    setForm({
      track: "coaching", full_name: "", email: "", phone: "", city: "",
      customer_number: "", selected_courses: [], custom_ue: {}, custom_names: {},
      custom_prices: {}, start_date: "",
      start_weeks_from: "", start_weeks_to: "",
      duration_weeks_from: "", duration_weeks_to: "",
      has_voucher: false, voucher_number: "", message: "", eignung_answers: {}, erstberatung: "", source: "staff-manual",
    });
  };

  return (
    <div className="App">
      <Brand minimal />
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="label-mono text-[color:var(--teal-dark)]">Mitarbeiter-Bereich</p>
            <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">
              Angebot manuell erstellen
            </h1>
            <p className="text-sm text-[color:var(--grey)] mt-2">
              Für Jobcoaching &amp; Gründercoaching mit anpassbaren UE-Werten pro Modul.
              Der Standard-Wert steht im Feld — überschreib ihn einfach.
            </p>
            {user?.name && (
              <p className="mt-2 text-xs text-[color:var(--teal-dark)] font-semibold" data-testid="staff-current-user">
                👤 Eingeloggt als {user.name}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/admin" className="btn-secondary text-sm inline-flex items-center gap-2">
              <ArrowLeft size={14} /> Admin
            </Link>
            <button onClick={logout} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="staff-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {lead ? (
          <div className="surface p-8 text-center" data-testid="staff-success">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--teal-light)] border border-[color:var(--teal)]/30 label-mono text-[color:var(--teal-dark)] mb-4">
              ✓ Erstellt
            </div>
            <h2 className="font-display text-2xl font-black">Angebot ist raus.</h2>
            <p className="mt-3 text-[color:var(--grey)]">
              Kunde: <strong>{lead.full_name}</strong> ({lead.email}) &middot; Track:{" "}
              <strong>{tracks.find((t) => t.key === lead.track)?.label}</strong>
            </p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              <a href={lead.pdf_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2">
                <FileDown size={18} /> PDF öffnen
              </a>
              {lead.eignung_answers && (
              <a href={lead.pdf_url.replace(/\/pdf$/, "/eignung.pdf")} target="_blank" rel="noreferrer"
                 data-testid="staff-eignung-pdf-link" className="btn-secondary inline-flex items-center gap-2">
                <FileDown size={18} /> Eignungsfeststellung
              </a>
              )}
              <button className="btn-secondary" onClick={reset}>Neues Angebot</button>
            </div>
            <p className="mt-6 label-mono text-[color:var(--grey)]">Lead-ID: {lead.id}</p>
          </div>
        ) : (
          <div className="surface p-6 md:p-8">
            {/* Track selector */}
            <label className="label-mono">Track</label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {tracks.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`staff-track-${t.key}`}
                  onClick={() => setForm((f) => ({ ...f, track: t.key, selected_courses: [], custom_ue: {}, custom_names: {}, custom_prices: {}, eignung_answers: {}, erstberatung: "" }))}
                  className={`py-3 rounded-2xl border-2 font-semibold transition ${
                    form.track === t.key
                      ? "border-[color:var(--teal)] bg-[color:var(--teal-light)] text-[color:var(--teal-dark)]"
                      : "border-[color:var(--border)] hover:border-[color:var(--teal)]/50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Module + UE / Name / Price editor */}
            <div className="mt-8">
              <label className="label-mono">
                {EDITABLE_TRACKS.includes(form.track)
                  ? "Module (individuell anpassbar)"
                  : "Module auswählen"}
              </label>
              <div className="mt-2 space-y-3">
                {activeTrack?.courses.map((c) => {
                  const selected = form.selected_courses.includes(c.key);
                  const currentUE = form.custom_ue[c.key];
                  const currentName = form.custom_names[c.key];
                  const currentPrice = form.custom_prices[c.key];
                  const computedPrice = (currentUE != null ? Number(currentUE) : c.ue) * pricePerUE;
                  const isEditable = EDITABLE_TRACKS.includes(form.track);
                  return (
                    <div
                      key={c.key}
                      className={`p-4 rounded-2xl border-2 transition ${
                        selected ? "border-[color:var(--teal)] bg-[color:var(--teal-light)]"
                                 : "border-[color:var(--border)] hover:border-[color:var(--teal)]/40"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          className="mt-1.5 w-5 h-5 accent-[color:var(--teal)]"
                          data-testid={`staff-course-${c.key}`}
                          checked={selected}
                          onChange={() => toggleCourse(c.key)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm">{c.name}</div>
                          <div className="text-xs text-[color:var(--grey)] mt-0.5">
                            {isEditable ? `Standard: ${c.ue} UE` : `${c.ue} UE`}
                          </div>
                        </div>
                      </div>
                      {selected && isEditable && (
                        <div className="mt-4 pl-9 grid gap-3">
                          {/* Custom Name */}
                          <div>
                            <label className="text-xs font-semibold text-[color:var(--teal-dark)]">
                              Modulname (auf PDF sichtbar)
                            </label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="text"
                                maxLength="200"
                                className="flex-1 py-2 px-3 rounded-xl border-2 border-[color:var(--teal)]/40 focus:border-[color:var(--teal)] outline-none"
                                data-testid={`staff-name-${c.key}`}
                                placeholder={c.name}
                                value={currentName ?? c.name}
                                onChange={(e) => setCustomName(c.key, e.target.value)}
                              />
                              {currentName != null && currentName !== c.name && (
                                <button type="button" onClick={() => clearCustomName(c.key)}
                                        className="text-xs underline text-[color:var(--teal-dark)]" title="Auf Standard zurücksetzen">zurück</button>
                              )}
                            </div>
                          </div>
                          {/* UE + Preis in einer Reihe */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-semibold text-[color:var(--teal-dark)]">UE</label>
                              <div className="flex items-center gap-2 mt-1">
                                <input
                                  type="number" min="1" max="999"
                                  className="w-full py-2 px-3 rounded-xl border-2 border-[color:var(--teal)]/40 focus:border-[color:var(--teal)] outline-none text-center font-semibold"
                                  data-testid={`staff-ue-${c.key}`}
                                  value={currentUE ?? c.ue}
                                  onChange={(e) => setCustomUE(c.key, e.target.value)}
                                />
                                <span className="text-sm font-semibold whitespace-nowrap">UE</span>
                                {currentUE != null && Number(currentUE) !== c.ue && (
                                  <button type="button" onClick={() => clearCustomUE(c.key)}
                                          className="text-xs underline text-[color:var(--teal-dark)]" title="Auf Standard">zurück</button>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-[color:var(--teal-dark)]">
                                Preis {pricePerUE > 0 && <span className="font-normal text-[color:var(--grey)]">(auto: {pricePerUE.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €/UE)</span>}
                              </label>
                              <div className="flex items-center gap-2 mt-1">
                                <input
                                  type="number" min="0" step="0.01"
                                  className="w-full py-2 px-3 rounded-xl border-2 border-[color:var(--teal)]/40 focus:border-[color:var(--teal)] outline-none text-right"
                                  data-testid={`staff-price-${c.key}`}
                                  placeholder={computedPrice > 0 ? computedPrice.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                                  value={currentPrice ?? ""}
                                  onChange={(e) => setCustomPrice(c.key, e.target.value)}
                                />
                                <span className="text-sm font-semibold whitespace-nowrap">€</span>
                                {currentPrice != null && currentPrice !== "" && (
                                  <button type="button" onClick={() => clearCustomPrice(c.key)}
                                          className="text-xs underline text-[color:var(--teal-dark)]" title="Zurück auf Auto-Preis">auto</button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {form.selected_courses.length > 0 && (
                <div className="mt-3 flex justify-end gap-4 text-sm font-semibold text-[color:var(--teal-dark)]">
                  <span>Gesamt: {totalUE} UE</span>
                  {hasAnyPrice && (
                    <span data-testid="staff-total-price">
                      Preis: {totalPrice.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Customer info */}
            <div className="mt-8 grid md:grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <label className="label-mono">Name</label>
                <input className="input-clean mt-2" data-testid="staff-name"
                  value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div>
                <label className="label-mono">E-Mail</label>
                <input className="input-clean mt-2" type="email" data-testid="staff-email"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="label-mono">Telefon</label>
                <input className="input-clean mt-2" data-testid="staff-phone"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="label-mono">Stadt</label>
                <input className="input-clean mt-2" data-testid="staff-city"
                  value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <label className="label-mono">Kundennummer (optional)</label>
                <input className="input-clean mt-2"
                  value={form.customer_number}
                  onChange={(e) => setForm({ ...form, customer_number: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="label-mono">Dauer der Maßnahme</label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number" min="1" max="99" placeholder="X"
                    data-testid="staff-duration-from"
                    className="w-24 py-2.5 px-3 rounded-xl border-2 border-[color:var(--border)] focus:border-[color:var(--teal)] outline-none text-center"
                    value={form.duration_weeks_from}
                    onChange={(e) => setForm({ ...form, duration_weeks_from: e.target.value })} />
                  <span className="text-sm text-[color:var(--grey)]">bis</span>
                  <input
                    type="number" min="1" max="99" placeholder="Y"
                    data-testid="staff-duration-to"
                    className="w-24 py-2.5 px-3 rounded-xl border-2 border-[color:var(--border)] focus:border-[color:var(--teal)] outline-none text-center"
                    value={form.duration_weeks_to}
                    onChange={(e) => setForm({ ...form, duration_weeks_to: e.target.value })} />
                  <span className="text-sm font-semibold">Wochen</span>
                </div>
              </div>
              {form.track !== "weiterbildung" ? (
                <div className="md:col-span-2">
                  <label className="label-mono">Voraussichtlicher Starttermin</label>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-[color:var(--grey)]">In</span>
                    <input
                      type="number" min="1" max="99" placeholder="X"
                      data-testid="staff-start-weeks-from"
                      className="w-20 py-2.5 px-3 rounded-xl border-2 border-[color:var(--border)] focus:border-[color:var(--teal)] outline-none text-center"
                      value={form.start_weeks_from}
                      onChange={(e) => setForm({ ...form, start_weeks_from: e.target.value })} />
                    <span className="text-sm text-[color:var(--grey)]">–</span>
                    <input
                      type="number" min="1" max="99" placeholder="Y"
                      data-testid="staff-start-weeks-to"
                      className="w-20 py-2.5 px-3 rounded-xl border-2 border-[color:var(--border)] focus:border-[color:var(--teal)] outline-none text-center"
                      value={form.start_weeks_to}
                      onChange={(e) => setForm({ ...form, start_weeks_to: e.target.value })} />
                    <span className="text-sm">Wochen nach Einsendung des AVGS</span>
                  </div>
                </div>
              ) : (
                <div className="md:col-span-2 rounded-xl bg-[color:var(--teal-light)] border border-[color:var(--teal)]/30 p-4 text-sm">
                  <div className="font-semibold text-[color:var(--teal-dark)]">Nächste Kursstarts</div>
                  <div className="text-[color:var(--grey)] mt-0.5">
                    Im Angebots-PDF erscheint automatisch ein Button, der auf <a href="https://www.we-academy.de/kursstarts" target="_blank" rel="noreferrer" className="underline">we-academy.de/kursstarts</a> verlinkt.
                  </div>
                </div>
              )}
              <div>
                <label className="label-mono" data-testid="staff-voucher-label">{form.track === "weiterbildung" ? "Bildungsgutschein vorhanden?" : "AVGS-Gutschein vorhanden?"}</label>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => setForm({ ...form, has_voucher: true })}
                    className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm ${
                      form.has_voucher ? "border-[color:var(--teal)] bg-[color:var(--teal-light)]" : "border-[color:var(--border)]"
                    }`}>Ja</button>
                  <button type="button" onClick={() => setForm({ ...form, has_voucher: false, voucher_number: "" })}
                    className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm ${
                      !form.has_voucher ? "border-[color:var(--teal)] bg-[color:var(--teal-light)]" : "border-[color:var(--border)]"
                    }`}>Nein</button>
                </div>
              </div>
              {form.has_voucher && (
                <div>
                  <label className="label-mono">Gutschein-Nummer</label>
                  <input className="input-clean mt-2"
                    value={form.voucher_number}
                    onChange={(e) => setForm({ ...form, voucher_number: e.target.value })} />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="label-mono">Interne Anmerkung (optional)</label>
                <textarea rows={3} className="input-clean mt-2 resize-none"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
            </div>

            {/* Erstberatung + Eignungsfeststellung */}
            <div className="mt-10 rounded-2xl border border-[color:var(--border)] p-5 md:p-6" data-testid="staff-eignung-section">
              <h2 className="font-display text-xl font-bold mb-4">Hat bereits eine Erstberatung stattgefunden?</h2>
              <div className="flex gap-3 mb-2">
                {[["ja", "Ja"], ["nein", "Nein"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`staff-erstberatung-${v}`}
                    onClick={() => setForm((f) => ({ ...f, erstberatung: v, eignung_answers: {} }))}
                    className={`px-6 py-2 rounded-full border-2 font-semibold text-sm transition-colors ${
                      form.erstberatung === v
                        ? "border-[color:var(--teal)] bg-[color:var(--teal-light)] text-[color:var(--teal-dark)]"
                        : "border-[color:var(--border)] hover:border-[color:var(--teal)]/50"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {form.erstberatung === "nein" && (
                <p className="text-sm text-[color:var(--grey)] mt-3" data-testid="staff-erstberatung-nein-hint">
                  Das Angebot kann direkt ohne Eignungsfeststellung versendet werden.
                </p>
              )}
              {form.erstberatung === "ja" && (
                <div className="mt-6">
                  <h3 className="font-display text-lg font-bold mb-1">Eignungsfeststellung</h3>
                  <p className="text-sm text-[color:var(--grey)] mb-5">
                    Bitte alle Fragen gemeinsam mit dem Kunden beantworten. Das Ergebnis wird als PDF an die Trello-Karte angehängt.
                  </p>
                  <EignungStep
                    questionsByTrack={eignungQuestions}
                    track={form.track}
                    answers={form.eignung_answers}
                    onChange={(a) => setForm((f) => ({ ...f, eignung_answers: a }))}
                  />
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="mt-8">
              <button type="button" data-testid="staff-preview-btn"
                onClick={() => (showPreview ? setShowPreview(false) : generatePreview())}
                disabled={previewLoading || form.selected_courses.length === 0}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-[color:var(--teal)]/40 bg-white hover:bg-[color:var(--teal-light)] text-[color:var(--teal-dark)] font-semibold disabled:opacity-50">
                {previewLoading ? (<><Loader2 size={16} className="animate-spin" /> Vorschau …</>)
                  : showPreview ? (<><EyeOff size={16} /> Vorschau ausblenden</>)
                  : (<><Eye size={16} /> PDF-Vorschau anzeigen</>)}
              </button>
              {showPreview && previewUrl && (
                <div className="mt-3 rounded-2xl border-2 border-[color:var(--teal)]/30 bg-[color:var(--light)] p-2">
                  <iframe src={previewUrl} title="Angebot" className="w-full rounded-xl bg-white" style={{ height: "600px", border: 0 }} />
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="mt-8 flex justify-end gap-3">
              <button
                data-testid="staff-submit"
                className="btn-primary inline-flex items-center gap-2"
                disabled={!canSubmit() || submitting}
                onClick={submit}
              >
                {submitting ? (<><Loader2 size={16} className="animate-spin" /> Sende …</>)
                             : (<>Angebot absenden</>)}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
